import { type ServiceParameter } from "../configuration";
import type { PriceCalculationParameterInfo } from "../pricing";
import type { BooleanValue } from "./BooleanValue";
import { type ConstantValue } from "./ConstantValue";
import type { DiscreteValue } from "./DiscreteValue";
import { type FileValue } from "./FileValue";
import { type LocationValue } from "./LocationValue";
import { type MultiValueValue } from "./MultiValueValue";
import type { RangeValue } from "./RangeValue";
import { type RemarksValue } from "./RemarksValue";
import type { TextValue } from "./TextValue";
import type { TieredValue } from "./TieredValue";
/**
 * The base type of parameter values provides all command properties.
 * All parameter value types inherit from this type to allow users to specify
 * a price and a value.
 */
export type ParameterValue<VALUE = unknown> = {
    /**
     * The kind of parameter value used for serialization and deserialization.
     */
    kind: ParameterValueKind;
    /**
     * Optional combine with parameters property to specify the parameters that can be combined with this parameter.
     *
     * Defaults to undefined.
     */
    combineWithParameters?: PriceCalculationParameterInfo.Id[];
    /**
     * Optional value property to specify the value of the parameter value.
     *
     * Defaults to undefined.
     */
    value?: VALUE;
    /**
     * The id of the parameter this value is for.
     */
    parameterId?: ServiceParameter.Id;
};

/**
 * The type of parameter values that are bound to a service configuration parameter.
 */
export type BoundParameterValue<VALUE = unknown> = ParameterValue<VALUE> & {
    /**
     * The parameter id to specify the parameter id of the parameter value.
     */
    parameterId: ServiceParameter.Id;
};

/**
 * The kind of parameter value used for serialization and deserialization.
 */
export type ParameterValueKind =
    | "discrete"
    | "multi-value"
    | "range"
    | "boolean"
    | "text"
    | "location"
    | "constant"
    | "tiered"
    | "file"
    | "remarks";

/**
 * Type guard to check if the value is a discrete value.
 */
export const isDiscrete = (value: ParameterValue): value is DiscreteValue => {
    return value.kind === "discrete";
};

/**
 * Type guard to check if the value is a range value.
 */
export const isRange = (value: ParameterValue): value is RangeValue => {
    return value.kind === "range";
};

/**
 * Type guard to check if the value is a boolean value.
 */
export const isBoolean = (value: ParameterValue): value is BooleanValue => {
    return value.kind === "boolean";
};

/**
 * Type guard to check if the value is a text value.
 */
export const isText = (value: ParameterValue): value is TextValue => {
    return value.kind === "text";
};

/**
 * Type guard to check if the value is a multi value value.
 */
export const isMultiValue = (value: ParameterValue): value is MultiValueValue => {
    return value.kind === "multi-value";
};

/**
 * Type guard to check if the value is a location value.
 */
export const isLocation = (value: ParameterValue): value is LocationValue => {
    return value.kind === "location";
};

/**
 * Type guard to check if the value is a constant value.
 */
export const isConstant = (value: ParameterValue): value is ConstantValue => {
    return value.kind === "constant";
};

/**
 * Type guard to check if the value is a tiered value.
 */
export const isTiered = (value: ParameterValue): value is TieredValue => {
    return value.kind === "tiered";
};

/**
 * Type guard to check if the value is a file value.
 */
export const isFile = (value: ParameterValue): value is FileValue => {
    return value.kind === "file";
};

/**
 * Type guard to check if the value is a remarks value.
 */
export const isRemarks = (value: ParameterValue): value is RemarksValue => {
    return value.kind === "remarks";
};
